import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonList,
  IonFab,
  IonFabButton,
  IonIcon,
  IonButtons,
  IonMenuButton,
  IonSpinner,
  IonRefresher,
  IonRefresherContent,
  IonActionSheet,
  useIonRouter,
  useIonToast,
} from '@ionic/react';
import {
  add,
  search,
  downloadOutline,
  checkboxOutline,
  chevronBack,
  chevronForward,
  people,
  closeCircle,
  funnel,
  close,
  options,
  call,
  peopleOutline,
  alertCircleOutline,
  createOutline,
} from 'ionicons/icons';
import { useLocation, useHistory } from 'react-router-dom';
import { useMembersList } from '@/hooks/useMembers';
import { usePermission } from '@/hooks/usePermission';
import MemberCard from '@/components/members/MemberCard';
import OutreachEntryModal from '@/components/members/OutreachEntryModal';
import {
  ChurchStatus,
  MembershipType,
  Gender,
  type CivilStatusFilter,
  type Member,
  type MemberFilters,
} from '@/types/member';
import {
  CHURCH_STATUS_LABELS,
  MEMBERSHIP_TYPE_LABELS,
  GENDER_LABELS,
  CIVIL_STATUS_LABELS,
  CIVIL_STATUS_LABELS_ALL,
  LABEL_FLAG_LABELS,
  FAMILY_ROLE_LABELS,
  PASTORAL_CASE_LABEL,
  isPastoralCase,
  pastoralCauseHint,
} from '@/utils/labels';
import { exportMembers, bulkUpdateMembers } from '@/api/members';
import { useSectors } from '@/hooks/useSectors';

/* ── Sortable column definitions ── */
type SortKey = 'full_name' | 'cell_phone' | 'sector_name' | 'church_status' | 'membership_type' | 'family_name' | 'gender' | 'birth_date' | 'occupation' | 'email' | 'civil_status' | 'home_phone';
// 'address' is display-only (composed/effective address can't be ordered in SQL).
type ColumnKey = SortKey | 'address' | 'outreach_reason' | 'outreach_notes';

interface ColumnDef {
  key: ColumnKey;
  label: string;
  minWidth?: number;
  defaultVisible?: boolean;
  /** false = header click does not sort (no backend ordering for this field). */
  sortable?: boolean;
}

const ALL_COLUMNS: ColumnDef[] = [
  { key: 'full_name', label: 'Nume', minWidth: 200, defaultVisible: true },
  { key: 'cell_phone', label: 'Telefon', minWidth: 120, defaultVisible: true },
  { key: 'birth_date', label: 'Vârstă', minWidth: 70, defaultVisible: true },
  { key: 'address', label: 'Adresă', minWidth: 200, defaultVisible: true, sortable: false },
  { key: 'family_name', label: 'Familie', minWidth: 100, defaultVisible: true },
  { key: 'gender', label: 'Gen', minWidth: 60, defaultVisible: false },
  { key: 'occupation', label: 'Ocupație', minWidth: 100, defaultVisible: false },
  { key: 'email', label: 'Email', minWidth: 140, defaultVisible: false },
  { key: 'home_phone', label: 'Tel. Fix', minWidth: 110, defaultVisible: false },
  { key: 'civil_status', label: 'Stare Civilă', minWidth: 100, defaultVisible: false },
  { key: 'sector_name', label: 'Sector', minWidth: 100, defaultVisible: true },
  { key: 'church_status', label: 'Status', minWidth: 100, defaultVisible: true },
  { key: 'membership_type', label: 'Tip', minWidth: 100, defaultVisible: true },
];

/**
 * Extra columns for the pastoral list: why the person is on it, and the note
 * the church keeps about them. Never offered on the ordinary members page.
 */
const OUTREACH_COLUMNS: ColumnDef[] = [
  { key: 'outreach_reason', label: 'Motiv', minWidth: 150, defaultVisible: true, sortable: false },
  { key: 'outreach_notes', label: 'Note', minWidth: 200, defaultVisible: true, sortable: false },
];

const DEFAULT_VISIBLE = new Set(ALL_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key));
const PAGE_SIZES = [25, 50, 100] as const;

/* ── Helpers ── */
function statusBadgeClass(status: ChurchStatus): string {
  const map: Record<ChurchStatus, string> = {
    [ChurchStatus.ACTIVE]: 'crm-badge--activ',
    [ChurchStatus.UNDER_DISCIPLINE]: 'crm-badge--sub_disciplina',
    [ChurchStatus.TRANSFERRED]: 'crm-badge--transferat',
    [ChurchStatus.LEFT]: 'crm-badge--plecat',
    [ChurchStatus.EXCOMMUNICATED]: 'crm-badge--exclus',
    [ChurchStatus.DECEASED]: 'crm-badge--decedat',
  };
  return map[status] ?? '';
}

function typeBadgeClass(type: MembershipType): string {
  const map: Record<MembershipType, string> = {
    [MembershipType.MEMBER]: 'crm-badge--membru',
    [MembershipType.DEPENDENT]: 'crm-badge--apartinator',
  };
  return map[type] ?? '';
}

// Re-export shared download utility
import { downloadBlob } from '@/utils/download';

/* ── Family-grouped search results ── */
const ROLE_ORDER: Record<string, number> = { head: 0, spouse: 1, child: 2, other: 3 };
const ROLE_COLORS: Record<string, string> = { head: '#5e81f4', spouse: '#9698d6', child: '#7ce7ac' };

interface FamilyGroup {
  familyId: string | null;
  familyName: string | null;
  sectorName: string | null;
  members: Member[];
}

function groupByFamily(members: Member[]): FamilyGroup[] {
  const map = new Map<string, FamilyGroup>();
  const noFamily: Member[] = [];
  for (const m of members) {
    if (!m.family_id) { noFamily.push(m); continue; }
    let group = map.get(m.family_id);
    if (!group) {
      group = { familyId: m.family_id, familyName: m.family_name, sectorName: m.sector_name, members: [] };
      map.set(m.family_id, group);
    }
    group.members.push(m);
  }
  const groups = Array.from(map.values());
  // Sort members within each family by role then age
  for (const g of groups) {
    g.members.sort((a, b) => {
      const rd = (ROLE_ORDER[a.family_role ?? ''] ?? 9) - (ROLE_ORDER[b.family_role ?? ''] ?? 9);
      if (rd !== 0) return rd;
      return (b.age ?? 0) - (a.age ?? 0);
    });
  }
  // Add individuals without family as single-member groups
  for (const m of noFamily) {
    groups.push({ familyId: null, familyName: null, sectorName: m.sector_name, members: [m] });
  }
  return groups;
}

const FamilyGroupedResults: React.FC<{ members: Member[]; onMemberClick: (id: string) => void; onFamilyClick: (id: string) => void }> = ({
  members, onMemberClick, onFamilyClick,
}) => {
  const groups = useMemo(() => groupByFamily(members), [members]);

  if (groups.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {groups.map((g, gi) => (
        <div key={g.familyId ?? `solo-${gi}`} style={{
          background: '#fff', borderRadius: 14, border: '1px solid #ececf2',
          overflow: 'hidden',
        }}>
          {/* Family header */}
          {g.familyId && (
            <div
              onClick={() => onFamilyClick(g.familyId!)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #ececf2',
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <IonIcon icon={peopleOutline} style={{ fontSize: 18, color: '#5e81f4' }} />
                <span style={{ fontWeight: 700, fontSize: 14, color: '#1c1d21' }}>
                  Familia {g.familyName ?? '—'}
                </span>
                <span style={{ fontSize: 12, color: '#8181a5' }}>
                  ({g.members.length} {g.members.length === 1 ? 'persoană' : 'persoane'})
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {g.sectorName && <span className="crm-badge crm-badge--sector">{g.sectorName}</span>}
                <span style={{ fontSize: 12, color: '#5e81f4', fontWeight: 600 }}>Detalii →</span>
              </div>
            </div>
          )}
          {/* Member rows */}
          <div style={{ padding: '4px 0' }}>
            {g.members.map((m) => {
              const roleColor = ROLE_COLORS[m.family_role ?? ''] ?? '#94a3b8';
              const initials = ((m.first_name[0] ?? '') + (m.last_name[0] ?? '')).toUpperCase();
              return (
                <div
                  key={m.id}
                  onClick={() => onMemberClick(m.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 20px', cursor: 'pointer', transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    background: m.gender === 'M' ? '#5e81f4' : '#d946ef',
                    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700,
                  }}>
                    {initials}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: 14, color: '#1c1d21' }}>
                        {m.last_name} {m.first_name}
                      </span>
                      {m.family_role && (
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                          color: roleColor, background: `${roleColor}15`,
                        }}>
                          {FAMILY_ROLE_LABELS[m.family_role]}
                        </span>
                      )}
                      {m.church_status !== 'active' && (
                        <span className={`crm-badge ${statusBadgeClass(m.church_status)}`} style={{ fontSize: 10 }}>
                          {CHURCH_STATUS_LABELS[m.church_status]}
                        </span>
                      )}
                      {m.is_approved === false && (
                        <span style={{
                          fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
                          color: '#b45309', background: '#fef3c7',
                        }}>
                          Neaprobat
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: '#8181a5', marginTop: 2 }}>
                      {m.age != null ? `${m.age} ani` : ''}
                      {m.occupation ? `${m.age != null ? ' · ' : ''}${m.occupation}` : ''}
                    </div>
                  </div>
                  {m.cell_phone && (
                    <a
                      href={`tel:${m.cell_phone}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        fontSize: 12, color: '#5e81f4', textDecoration: 'none', flexShrink: 0,
                      }}
                    >
                      <IonIcon icon={call} style={{ fontSize: 13 }} />
                      {m.cell_phone}
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════
   MemberListPage
   ═══════════════════════════════════════ */
interface MemberListPageProps {
  /**
   * Renders the pastoral follow-up list ("Nebotezați / Fii Risipitori") instead
   * of the ordinary members roster: same table machinery, different population
   * and two extra columns. Mounted at /nebotezati.
   */
  outreach?: boolean;
}

const MemberListPage: React.FC<MemberListPageProps> = ({ outreach = false }) => {
  const router = useIonRouter();
  const location = useLocation();
  const history = useHistory();
  const { can } = usePermission();
  const isAdmin = can('members.create');
  const canExport = can('members.export');
  const canApprove = can('members.approve');
  // Moving someone between the registry and the pastoral list is a
  // status-level decision — mirrors REGISTRY_AFFECTING_FIELDS on the API.
  const canManageList = can('members.change_status');

  const urlParams = new URLSearchParams(location.search);
  const sectorIdFromUrl = urlParams.get('sector_id') ?? undefined;
  const membershipTypeFromUrl = urlParams.get('membership_type') ?? '';
  const searchFromUrl = urlParams.get('search') ?? '';
  const pendingApprovalFromUrl = urlParams.get('pending_approval') === '1';

  /* ── Local state ── */
  // Two values, on purpose: `searchDraft` is what the box shows (instant),
  // `searchText` is what the API is asked for (debounced).
  const [searchDraft, setSearchDraft] = useState(searchFromUrl);
  const [searchText, setSearchText] = useState(searchFromUrl);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(25);
  const [sortKey, setSortKey] = useState<SortKey>('full_name');
  const [sortAsc, setSortAsc] = useState(true);
  const [userChangedSort, setUserChangedSort] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkMode, setBulkMode] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportSheet, setShowExportSheet] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [present] = useIonToast();

  /* ── Applied filter state (what the API actually uses) ── */
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [selectedSectorId, setSelectedSectorId] = useState<string | undefined>(undefined);
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');
  const [filterStatus, setFilterStatus] = useState<ChurchStatus | ''>('');
  const [filterType, setFilterType] = useState<MembershipType | ''>(membershipTypeFromUrl as MembershipType | '');
  const [filterGender, setFilterGender] = useState<Gender | ''>('');
  const [filterCivil, setFilterCivil] = useState<CivilStatusFilter | ''>('');
  const [filterLabels, setFilterLabels] = useState<Record<string, boolean>>({});
  const [filterUpdatedWithin, setFilterUpdatedWithin] = useState<'' | '7' | '30' | '90'>('');

  /* ── Temporary dialog state (staged, not yet applied) ── */
  const [draftStatus, setDraftStatus] = useState<ChurchStatus | ''>('');
  const [draftType, setDraftType] = useState<MembershipType | ''>('');
  const [draftGender, setDraftGender] = useState<Gender | ''>('');
  const [draftCivil, setDraftCivil] = useState<CivilStatusFilter | ''>('');
  const [draftSectorId, setDraftSectorId] = useState<string | undefined>(undefined);
  const [draftMinAge, setDraftMinAge] = useState('');
  const [draftMaxAge, setDraftMaxAge] = useState('');
  const [draftLabels, setDraftLabels] = useState<Record<string, boolean>>({});
  const [draftUpdatedWithin, setDraftUpdatedWithin] = useState<'' | '7' | '30' | '90'>('');
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);

  /* ── Column visibility (persisted to localStorage) ── */
  // The two modes keep separate preferences — the pastoral list has columns the
  // roster does not. Read and write the SAME key (they used to differ, so the
  // saved choice was silently discarded on reload).
  const columnStorageKey = outreach ? 'betel_outreach_columns' : 'betel_member_columns_v2';
  const availableColumns = outreach ? [...ALL_COLUMNS, ...OUTREACH_COLUMNS] : ALL_COLUMNS;

  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(columnStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as string[];
        if (Array.isArray(parsed) && parsed.length >= 2) {
          return new Set(parsed.filter((k) => availableColumns.some((c) => c.key === k)));
        }
      }
    } catch { /* ignore */ }
    return new Set(availableColumns.filter((c) => c.defaultVisible).map((c) => c.key));
  });
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  useEffect(() => {
    localStorage.setItem(columnStorageKey, JSON.stringify([...visibleColumns]));
  }, [columnStorageKey, visibleColumns]);

  /** Pastoral list only: reveal people taken off the list by hand. */
  const [showRemoved, setShowRemoved] = useState(false);
  /** Ordinary roster only: reveal pastoral cases, which are hidden by default. */
  const [showPastoral, setShowPastoral] = useState(false);
  /** Pastoral list only: the person whose flag/notes are being edited. */
  const [editingOutreach, setEditingOutreach] = useState<Member | null>(null);

  const { data: sectors } = useSectors();
  const activeSector = sectorIdFromUrl ? sectors?.find((s) => s.id === sectorIdFromUrl) : undefined;

  const clearSectorFilter = useCallback(() => { history.replace('/members'); }, [history]);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  /* "/" or Ctrl/Cmd+K jumps straight into the console's search box. */
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const el = document.activeElement as HTMLElement | null;
      const isTyping = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      const isSlash = e.key === '/' && !isTyping;
      const isCmdK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k';
      if (!isSlash && !isCmdK) return;
      e.preventDefault();
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => () => { if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current); }, []);

  /* ── Count active filters ──
     `filterType` is deliberately excluded: the console's segmented control
     already shows it, so counting it here would double-report. */
  const activeFilterCount = [
    filterStatus, filterGender, filterCivil,
    selectedSectorId, minAge, maxAge, filterUpdatedWithin,
    ...Object.values(filterLabels).filter(Boolean),
  ].filter(Boolean).length;

  /* ── Build API filters ── */
  const filters: MemberFilters = {
    search: searchText || undefined,
    church_status: (filterStatus as ChurchStatus) || undefined,
    membership_type: (filterType as MembershipType) || undefined,
    gender: (filterGender as Gender) || undefined,
    civil_status: (filterCivil as CivilStatusFilter) || undefined,
    sector_id: sectorIdFromUrl || selectedSectorId,
    pending_approval: pendingApprovalFromUrl || undefined,
    outreach: outreach || undefined,
    include_excluded: (outreach && showRemoved) || undefined,
    include_pastoral: (!outreach && showPastoral) || undefined,
    is_prodigal: filterLabels['is_prodigal'] || undefined,
    is_unbaptized_adult: filterLabels['is_unbaptized_adult'] || undefined,
    has_disability: filterLabels['has_disability'] || undefined,
    has_special_need: filterLabels['has_special_need'] || undefined,
    no_membership: filterLabels['no_membership'] || undefined,
    min_age: minAge ? Number(minAge) : undefined,
    max_age: maxAge ? Number(maxAge) : undefined,
    updated_within: filterUpdatedWithin ? Number(filterUpdatedWithin) : undefined,
    ordering: (searchText && !userChangedSort) ? undefined : `${sortAsc ? '' : '-'}${sortKey}`,
    page: currentPage,
    page_size: pageSize,
  };

  const { data, isLoading, isFetching, refetch } = useMembersList(filters);

  const members: Member[] = data?.data ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  /* ── Responsive ── */
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 992 : false,
  );

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 991px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, []);

  /* ── Handlers ── */
  const handleSearch = useCallback((value: string) => {
    setSearchDraft(value);
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setSearchText(value);
      setCurrentPage(1);
    }, 300);
  }, []);

  const clearSearch = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setSearchDraft('');
    setSearchText('');
    setCurrentPage(1);
    searchInputRef.current?.focus();
  }, []);

  /** Open filter dialog — seed draft from current applied state */
  const openFilterDialog = useCallback(() => {
    setDraftStatus(filterStatus);
    setDraftType(filterType);
    setDraftGender(filterGender);
    setDraftCivil(filterCivil);
    setDraftSectorId(selectedSectorId);
    setDraftMinAge(minAge);
    setDraftMaxAge(maxAge);
    setDraftLabels({ ...filterLabels });
    setDraftUpdatedWithin(filterUpdatedWithin);
    setShowFilterDialog(true);
  }, [filterStatus, filterType, filterGender, filterCivil, selectedSectorId, minAge, maxAge, filterLabels, filterUpdatedWithin]);

  /** Apply draft filters — show brief loading indicator */
  const applyFilters = useCallback(() => {
    setIsApplyingFilters(true);
    // Batch apply all draft values
    setFilterStatus(draftStatus);
    setFilterType(draftType);
    setFilterGender(draftGender);
    setFilterCivil(draftCivil);
    setSelectedSectorId(draftSectorId);
    setMinAge(draftMinAge);
    setMaxAge(draftMaxAge);
    setFilterLabels({ ...draftLabels });
    setFilterUpdatedWithin(draftUpdatedWithin);
    setCurrentPage(1);
    setShowFilterDialog(false);
    // Clear loading after a tick (data fetch will show its own indicator)
    setTimeout(() => setIsApplyingFilters(false), 300);
  }, [draftStatus, draftType, draftGender, draftCivil, draftSectorId, draftMinAge, draftMaxAge, draftLabels, draftUpdatedWithin]);

  /** Reset all draft values inside the dialog */
  const resetDraftFilters = useCallback(() => {
    setDraftStatus('');
    setDraftType('');
    setDraftGender('');
    setDraftCivil('');
    setDraftSectorId(undefined);
    setDraftMinAge('');
    setDraftMaxAge('');
    setDraftLabels({});
    setDraftUpdatedWithin('');
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilterStatus('');
    setFilterType('');
    setFilterGender('');
    setFilterCivil('');
    setSelectedSectorId(undefined);
    setMinAge('');
    setMaxAge('');
    setFilterLabels({});
    setFilterUpdatedWithin('');
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((key: SortKey) => {
    if (sortKey === key) setSortAsc((prev) => !prev);
    else { setSortKey(key); setSortAsc(true); }
    setUserChangedSort(true);
    setCurrentPage(1);
  }, [sortKey]);

  const handleRefresh = async (event: CustomEvent) => {
    await refetch();
    event.detail.complete();
  };

  // Name this roster for the person page's back button — see MemberDetailPage.
  const navigateToMember = useCallback((id: string) => {
    history.push(`/members/${id}`, {
      backTo: {
        path: outreach ? '/nebotezati' : '/members',
        label: outreach ? 'Lista pastorală' : 'Persoane',
      },
    });
  }, [history, outreach]);

  /* ── Bulk ── */
  const toggleSelectAll = useCallback(() => {
    setSelectedIds(selectedIds.size === members.length ? new Set() : new Set(members.map((m) => m.id)));
  }, [members, selectedIds.size]);
  const toggleSelectOne = useCallback((id: string) => {
    setSelectedIds((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }, []);
  const handleCancelBulk = useCallback(() => { setBulkMode(false); setSelectedIds(new Set()); }, []);

  /* ── Export ── */
  const handleExport = useCallback(async (format: 'excel' | 'csv') => {
    setIsExporting(true);
    try {
      const blob = await exportMembers({ ...filters, page: undefined, page_size: undefined, format });
      const ext = format === 'excel' ? 'xlsx' : 'csv';
      downloadBlob(blob, `membri_${new Date().toISOString().slice(0, 10)}.${ext}`);
    } catch {
      present({ message: 'Eroare la export. Încearcă din nou.', duration: 3000, color: 'danger', position: 'bottom' });
    } finally {
      setIsExporting(false);
    }
  }, [filters]);

  /* ── Bulk update ── */
  const handleBulkUpdate = useCallback(async (field: string, value: string) => {
    if (selectedIds.size === 0) return;
    try {
      await bulkUpdateMembers({ member_ids: Array.from(selectedIds), [field]: value });
      present({ message: `${selectedIds.size} membri actualizați cu succes.`, duration: 2000, color: 'success', position: 'bottom' });
      handleCancelBulk();
      void refetch();
    } catch {
      present({ message: 'Eroare la actualizarea în masă.', duration: 3000, color: 'danger', position: 'bottom' });
    }
  }, [selectedIds, handleCancelBulk, refetch, present]);

  const sortArrow = (key: SortKey) => (sortKey === key ? (sortAsc ? ' \u2191' : ' \u2193') : '');

  /* ── Column toggle ── */
  const toggleColumn = (key: string) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key) && next.size > 2) next.delete(key); // keep at least 2
      else next.add(key);
      return next;
    });
  };

  const showAllColumns = () => setVisibleColumns(new Set(availableColumns.map((c) => c.key)));
  const showDefaultColumns = () => setVisibleColumns(new Set(DEFAULT_VISIBLE));

  const visibleCols = availableColumns.filter((c) => visibleColumns.has(c.key));

  /* ── Console readouts ── */
  const countNoun = filterType === MembershipType.DEPENDENT
    ? (totalCount === 1 ? 'aparținător' : 'aparținători')
    : (totalCount === 1 ? 'persoană' : 'persoane');
  /** True while the console is narrowing the roster — lights up its top edge. */
  const consoleActive = !!searchDraft
    || activeFilterCount > 0
    || pendingApprovalFromUrl
    || !!sectorIdFromUrl
    || (outreach ? showRemoved : showPastoral);

  /* ── Pagination ── */
  const pageNumbers = useMemo(() => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); }
    else {
      pages.push(1);
      if (currentPage > 3) pages.push('ellipsis');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);
    }
    return pages;
  }, [totalPages, currentPage]);

  /* ── Render cell ── */
  const renderCell = (col: ColumnDef, member: Member) => {
    switch (col.key) {
      case 'full_name': {
        const initials = ((member.first_name[0] ?? '') + (member.last_name[0] ?? '')).toUpperCase();
        const age = member.age != null ? `${member.age} ani` : null;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
              backgroundColor: member.gender === 'M' ? '#5e81f4' : '#d946ef',
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700,
            }}>{initials}</div>
            <div>
              <div className="crm-table__name">
                {member.last_name} {member.first_name}
                {/* On the ordinary roster these rows only surface via search or
                    the toggle — say why, so nobody reads it as a stray record. */}
                {!outreach && isPastoralCase(member) && (
                  <span
                    className="crm-badge crm-badge--pastoral"
                    style={{ fontSize: 10, marginLeft: 6, verticalAlign: 'middle' }}
                    title={`Apare pe lista Nebotezați / Fii Risipitori (${pastoralCauseHint(member)})`}
                  >
                    {PASTORAL_CASE_LABEL}
                  </span>
                )}
              </div>
              <div className="crm-table__sub">
                {age}{age && member.occupation ? ' · ' : ''}{member.occupation ?? ''}
                {!age && !member.occupation && member.email ? member.email : ''}
              </div>
            </div>
          </div>
        );
      }
      case 'cell_phone':
        return member.cell_phone
          ? <a href={`tel:${member.cell_phone}`} onClick={(e) => e.stopPropagation()} style={{ fontSize: 13, color: '#1c1d21', textDecoration: 'none' }}>{member.cell_phone}</a>
          : <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>;
      case 'home_phone':
        return member.home_phone
          ? <a href={`tel:${member.home_phone}`} onClick={(e) => e.stopPropagation()} style={{ fontSize: 13, color: '#1c1d21', textDecoration: 'none' }}>{member.home_phone}</a>
          : <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>;
      case 'family_name':
        return member.family_name
          ? <span style={{ fontSize: 13, color: '#475569' }}>{member.family_name}{member.family_role && <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 4 }}>({FAMILY_ROLE_LABELS[member.family_role] ?? ''})</span>}</span>
          : <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>;
      case 'gender':
        return <span style={{ fontSize: 12, color: '#64748b' }}>{GENDER_LABELS[member.gender] ?? '—'}</span>;
      case 'birth_date':
        return <span style={{ fontSize: 12, color: '#64748b' }}>{member.age != null ? `${member.age}` : '—'}</span>;
      case 'address': {
        const street = member.effective_address || member.address || member.family_address || '';
        const city = member.effective_city || member.city || member.family_city || '';
        const full = [street, city].filter(Boolean).join(', ');
        return full
          ? <span style={{ fontSize: 12, color: '#64748b' }}>{full}</span>
          : <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>;
      }
      case 'occupation':
        return <span style={{ fontSize: 12, color: '#64748b' }}>{member.occupation || '—'}</span>;
      case 'email':
        return member.email
          ? <a href={`mailto:${member.email}`} onClick={(e) => e.stopPropagation()} style={{ fontSize: 12, color: '#5e81f4', textDecoration: 'none' }}>{member.email}</a>
          : <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>;
      case 'civil_status':
        return <span style={{ fontSize: 12, color: '#64748b' }}>{member.civil_status ? CIVIL_STATUS_LABELS[member.civil_status] : '—'}</span>;
      case 'sector_name':
        return member.sector_name
          ? <span className="crm-badge crm-badge--sector">{member.sector_name}</span>
          : <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>;
      case 'church_status':
        return member.church_status === 'active'
          ? <span style={{ fontSize: 12, color: '#cbd5e1' }}>—</span>
          : <span className={`crm-badge ${statusBadgeClass(member.church_status)}`}>{CHURCH_STATUS_LABELS[member.church_status]}</span>;
      case 'membership_type':
        return <span className={`crm-badge ${typeBadgeClass(member.membership_type)}`}>{MEMBERSHIP_TYPE_LABELS[member.membership_type]}</span>;
      case 'outreach_reason': {
        // One label for everyone on the list; the cause rides along quietly so
        // you can still tell a derived entry from a hand-flagged one.
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
            <span className="crm-badge crm-badge--pastoral" style={{ fontSize: 11 }}>{PASTORAL_CASE_LABEL}</span>
            <span style={{ fontSize: 10.5, color: '#94a3b8' }}>{pastoralCauseHint(member)}</span>
            {member.outreach_excluded_at && (
              <span className="crm-badge" style={{ fontSize: 11, backgroundColor: '#e2e8f0', color: '#475569' }}>
                Scos din listă
              </span>
            )}
          </div>
        );
      }
      case 'outreach_notes': {
        const note = member.prodigal_notes || member.outreach_exclusion_reason;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: note ? '#64748b' : '#cbd5e1', flex: 1 }}>{note || '—'}</span>
            {canManageList && (
              <button
                onClick={(e) => { e.stopPropagation(); setEditingOutreach(member); }}
                title="Editează"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0 }}
              >
                <IonIcon icon={createOutline} style={{ fontSize: 16, color: '#94a3b8' }} />
              </button>
            )}
          </div>
        );
      }
      default:
        return '—';
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="crm-toolbar">
          <IonButtons slot="start"><IonMenuButton /></IonButtons>
          <IonTitle>{outreach ? 'Nebotezați / Fii Risipitori' : 'Persoane'}</IonTitle>
          {/* Every action lives in the console below — it is the one surface
              that survives the mobile breakpoint, where this toolbar is hidden. */}
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}><IonRefresherContent /></IonRefresher>

        <div className="crm-page-layout">
          <div className="crm-main-content">

            {/* ══════════════════════════════════════════════
                CONSOLE — the roster's control panel: search,
                scope, filters and tools on one surface.
                ══════════════════════════════════════════════ */}
            <div className={`roster-console${consoleActive ? ' roster-console--active' : ''}`}>

              {/* ── Search ── */}
              <div className="roster-console__search">
                <div className="roster-console__glyph" aria-hidden="true"><IonIcon icon={search} /></div>
                <input
                  ref={searchInputRef}
                  type="text"
                  className="roster-console__input"
                  placeholder={outreach
                    ? 'Caută pe listă — nume, telefon, adresă…'
                    : 'Caută o persoană — nume, telefon, adresă, ocupație…'}
                  value={searchDraft}
                  onChange={(e) => handleSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Escape' && searchDraft) { e.preventDefault(); clearSearch(); } }}
                  aria-label="Caută persoane"
                  autoComplete="off"
                  spellCheck={false}
                />
                {searchDraft ? (
                  <button type="button" className="roster-console__clear" onClick={clearSearch} aria-label="Șterge căutarea">
                    <IonIcon icon={close} />
                  </button>
                ) : (
                  <kbd className="roster-console__kbd" title="Apasă / oriunde pentru a căuta">/</kbd>
                )}
                {isFetching && <div className="roster-console__wire" />}
              </div>

              {/* ── Control bar ── */}
              <div className="roster-console__bar">
                {bulkMode ? (
                  <>
                    <div className="roster-console__cluster">
                      <span className="roster-console__hits">
                        {selectedIds.size} {selectedIds.size === 1 ? 'selectată' : 'selectate'}
                      </span>
                      <button type="button" className="roster-tool" onClick={toggleSelectAll}>
                        <IonIcon icon={checkboxOutline} />
                        {members.length > 0 && selectedIds.size === members.length ? 'Deselectează tot' : 'Selectează tot'}
                      </button>
                    </div>
                    <div className="roster-console__cluster roster-console__cluster--end">
                      <button
                        type="button"
                        className="roster-tool roster-tool--primary"
                        disabled={selectedIds.size === 0}
                        onClick={() => setShowBulkActions(true)}
                      >
                        Editează ({selectedIds.size})
                      </button>
                      <button type="button" className="roster-tool roster-tool--danger" onClick={handleCancelBulk}>
                        Anulează
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="roster-console__cluster">
                    <span className="roster-console__hits">
                      {totalCount} {countNoun}{activeFilterCount > 0 || searchText ? ' · filtrat' : ''}
                    </span>

                    <div className="roster-seg" role="tablist" aria-label="Tip persoană">
                      {([
                        { value: '' as MembershipType | '', label: 'Toți' },
                        { value: MembershipType.MEMBER, label: 'Membri' },
                        { value: MembershipType.DEPENDENT, label: 'Aparținători' },
                      ] as const).map((tab) => (
                        <button
                          key={tab.value || 'all'}
                          type="button"
                          role="tab"
                          aria-selected={filterType === tab.value}
                          className={`roster-seg__btn${filterType === tab.value ? ' roster-seg__btn--active' : ''}`}
                          onClick={() => { setFilterType(tab.value); setDraftType(tab.value); setCurrentPage(1); }}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    <button
                      type="button"
                      className={`roster-tool${activeFilterCount > 0 ? ' roster-tool--on' : ''}`}
                      onClick={openFilterDialog}
                    >
                      <IonIcon icon={funnel} /> Filtre
                      {activeFilterCount > 0 && <span className="roster-tool__badge">{activeFilterCount}</span>}
                    </button>

                    {!isMobile && (
                      <button
                        type="button"
                        className={`roster-tool${showColumnPicker ? ' roster-tool--on' : ''}`}
                        onClick={() => setShowColumnPicker((s) => !s)}
                      >
                        <IonIcon icon={options} /> Coloane
                      </button>
                    )}

                    {isAdmin && (
                      <button type="button" className="roster-tool" onClick={() => setBulkMode(true)}>
                        <IonIcon icon={checkboxOutline} /> Selectează
                      </button>
                    )}

                    {canApprove && !pendingApprovalFromUrl && (
                      <button
                        type="button"
                        className="roster-tool"
                        onClick={() => history.replace('/members?pending_approval=1')}
                      >
                        <IonIcon icon={alertCircleOutline} /> Neaprobate
                      </button>
                    )}
                    </div>

                    <div className="roster-console__cluster roster-console__cluster--end">
                    {outreach ? (
                      // People taken off the list are hidden, never deleted —
                      // this is how they stay one click away.
                      <label className="roster-switch" title="Arată și persoanele scoase din listă">
                        <input
                          type="checkbox"
                          checked={showRemoved}
                          onChange={(e) => { setShowRemoved(e.target.checked); setCurrentPage(1); }}
                        />
                        <span className="roster-switch__track" />
                        Cei scoși din listă
                      </label>
                    ) : (
                      // Pastoral cases live on their own list and are hidden
                      // here. A search still finds them either way.
                      <label className="roster-switch" title="Arată și persoanele de pe lista pastorală">
                        <input
                          type="checkbox"
                          checked={showPastoral}
                          onChange={(e) => { setShowPastoral(e.target.checked); setCurrentPage(1); }}
                        />
                        <span className="roster-switch__track" />
                        Lista pastorală
                      </label>
                    )}

                    {canExport && (
                      <button type="button" className="roster-tool" disabled={isExporting} onClick={() => setShowExportSheet(true)}>
                        <IonIcon icon={downloadOutline} /> Export
                      </button>
                    )}

                    <div className="roster-rows">
                      Rânduri
                      <div className="roster-rows__opts">
                        {PAGE_SIZES.map((size) => (
                          <button
                            key={size}
                            type="button"
                            className={`roster-rows__opt${pageSize === size ? ' roster-rows__opt--active' : ''}`}
                            onClick={() => { setPageSize(size); setCurrentPage(1); }}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                    </div>
                  </>
                )}
              </div>

              {/* ── Scope banners ── */}
              {activeSector && (
                <div
                  className="roster-console__banner"
                  style={{
                    backgroundColor: `${activeSector.color || '#4f46e5'}10`,
                    color: activeSector.color || '#4f46e5',
                  }}
                >
                  <span>Doar sectorul {activeSector.name}</span>
                  <button className="roster-console__banner-x" onClick={clearSectorFilter} aria-label="Renunță la filtrul de sector">
                    <IonIcon icon={closeCircle} style={{ fontSize: 18 }} />
                  </button>
                </div>
              )}

              {pendingApprovalFromUrl && (
                <div className="roster-console__banner" style={{ backgroundColor: '#fef6e6', color: '#b4802a' }}>
                  <IonIcon icon={alertCircleOutline} />
                  <span>Doar persoane neaprobate</span>
                  <button className="roster-console__banner-x" onClick={() => history.replace('/members')} aria-label="Arată toate persoanele">
                    <IonIcon icon={closeCircle} style={{ fontSize: 18 }} />
                  </button>
                </div>
              )}

              {/* ── Applied filters ── */}
              {activeFilterCount > 0 && (
                <div className="roster-console__chips">
                  {filterStatus && (
                    <span className="roster-chip">
                      {CHURCH_STATUS_LABELS[filterStatus]}
                      <button className="roster-chip__x" onClick={() => setFilterStatus('')} aria-label="Elimină filtrul">&times;</button>
                    </span>
                  )}
                  {filterGender && (
                    <span className="roster-chip">
                      {GENDER_LABELS[filterGender]}
                      <button className="roster-chip__x" onClick={() => setFilterGender('')} aria-label="Elimină filtrul">&times;</button>
                    </span>
                  )}
                  {filterCivil && (
                    <span className="roster-chip">
                      {CIVIL_STATUS_LABELS_ALL[filterCivil]}
                      <button className="roster-chip__x" onClick={() => setFilterCivil('')} aria-label="Elimină filtrul">&times;</button>
                    </span>
                  )}
                  {selectedSectorId && (
                    <span className="roster-chip">
                      Sector: {sectors?.find((s) => s.id === selectedSectorId)?.name ?? '—'}
                      <button className="roster-chip__x" onClick={() => setSelectedSectorId(undefined)} aria-label="Elimină filtrul">&times;</button>
                    </span>
                  )}
                  {(minAge || maxAge) && (
                    <span className="roster-chip">
                      Vârstă {minAge || '0'}–{maxAge || '∞'}
                      <button className="roster-chip__x" onClick={() => { setMinAge(''); setMaxAge(''); }} aria-label="Elimină filtrul">&times;</button>
                    </span>
                  )}
                  {filterUpdatedWithin && (
                    <span className="roster-chip">
                      Actualizat în {filterUpdatedWithin} zile
                      <button className="roster-chip__x" onClick={() => setFilterUpdatedWithin('')} aria-label="Elimină filtrul">&times;</button>
                    </span>
                  )}
                  {Object.entries(filterLabels).filter(([, v]) => v).map(([k]) => (
                    <span key={k} className="roster-chip roster-chip--label">
                      {LABEL_FLAG_LABELS[k]}
                      <button className="roster-chip__x" onClick={() => setFilterLabels((p) => ({ ...p, [k]: false }))} aria-label="Elimină filtrul">&times;</button>
                    </span>
                  ))}
                  <button className="roster-chips__clear" onClick={handleClearFilters}>Șterge tot</button>
                </div>
              )}

              {/* ── Column picker ── */}
              {showColumnPicker && !isMobile && (
                <div className="roster-columns">
                  <div className="roster-columns__head">
                    <span className="roster-columns__title">Coloane vizibile</span>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <button className="roster-columns__link" onClick={showAllColumns}>Toate</button>
                      <button className="roster-columns__link" onClick={showDefaultColumns}>Implicit</button>
                      <button className="roster-columns__link" onClick={() => setShowColumnPicker(false)} aria-label="Închide">
                        <IonIcon icon={close} style={{ fontSize: 15, verticalAlign: 'middle' }} />
                      </button>
                    </div>
                  </div>
                  <div className="roster-columns__grid">
                    {availableColumns.map((col) => (
                      <label key={col.key} className={`roster-columns__opt${visibleColumns.has(col.key) ? ' roster-columns__opt--on' : ''}`}>
                        <input type="checkbox" checked={visibleColumns.has(col.key)} onChange={() => toggleColumn(col.key)} />
                        {col.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Loading ── */}
            {isLoading && !data && (
              <div style={{ textAlign: 'center', padding: 60 }}>
                <IonSpinner name="crescent" />
                <p style={{ fontSize: 13, color: '#94a3b8', marginTop: 12 }}>Se incarcă...</p>
              </div>
            )}
            {/* In-flight requests are signalled by the console's own wire. */}

            {/* ── Family-grouped search results ── */}
            {data && searchText && !userChangedSort && !bulkMode && members.length > 0 && (
              <>
                <FamilyGroupedResults
                  members={members}
                  onMemberClick={navigateToMember}
                  onFamilyClick={(id) => router.push(`/families/${id}`)}
                />
                {totalPages > 1 && (
                  <div className="crm-pagination" style={{ marginTop: 16 }}>
                    <button className="crm-pagination__btn" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
                      <IonIcon icon={chevronBack} style={{ fontSize: 12 }} /> PREV
                    </button>
                    {pageNumbers.map((p, idx) => p === 'ellipsis'
                      ? <span key={`e-${idx}`} style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>
                      : <button key={p} className={`crm-pagination__num ${p === currentPage ? 'crm-pagination__num--active' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>
                    )}
                    <button className="crm-pagination__btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
                      NEXT <IonIcon icon={chevronForward} style={{ fontSize: 12 }} />
                    </button>
                  </div>
                )}
              </>
            )}

            {/* ── Desktop table (when not in grouped search mode) ── */}
            {data && !isMobile && !(searchText && !userChangedSort && !bulkMode && members.length > 0) && (
              <div className="crm-card" style={{ overflow: 'auto' }}>
                <table className="crm-table">
                  <thead>
                    <tr>
                      {bulkMode && <th style={{ width: 40 }}><input type="checkbox" checked={members.length > 0 && selectedIds.size === members.length} onChange={toggleSelectAll} /></th>}
                      {visibleCols.map((col) => {
                        const sortable = col.sortable !== false;
                        return (
                          <th
                            key={col.key}
                            onClick={sortable ? () => handleSort(col.key as SortKey) : undefined}
                            style={{ minWidth: col.minWidth, cursor: sortable ? 'pointer' : 'default' }}
                          >
                            {col.label}{sortable ? sortArrow(col.key as SortKey) : ''}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => (
                      <tr key={member.id} onClick={() => bulkMode ? toggleSelectOne(member.id) : navigateToMember(member.id)} style={{ cursor: 'pointer' }} className="crm-animate-in">
                        {bulkMode && <td><input type="checkbox" checked={selectedIds.has(member.id)} onChange={() => toggleSelectOne(member.id)} onClick={(e) => e.stopPropagation()} /></td>}
                        {visibleCols.map((col) => <td key={col.key}>{renderCell(col, member)}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {members.length === 0 && (
                  <div className="crm-empty-state">
                    <div className="crm-empty-state__icon"><IonIcon icon={people} /></div>
                    <h3 className="crm-empty-state__title">Niciun membru găsit</h3>
                    <p className="crm-empty-state__text">Încearcă să modifici căutarea sau filtrele aplicate.</p>
                  </div>
                )}
                {totalPages > 1 && (
                  <div className="crm-pagination">
                    <button className="crm-pagination__btn" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
                      <IonIcon icon={chevronBack} style={{ fontSize: 12 }} /> PREV
                    </button>
                    {pageNumbers.map((p, idx) => p === 'ellipsis'
                      ? <span key={`e-${idx}`} style={{ padding: '0 4px', color: '#94a3b8' }}>...</span>
                      : <button key={p} className={`crm-pagination__num ${p === currentPage ? 'crm-pagination__num--active' : ''}`} onClick={() => setCurrentPage(p)}>{p}</button>
                    )}
                    <button className="crm-pagination__btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
                      NEXT <IonIcon icon={chevronForward} style={{ fontSize: 12 }} />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ── Mobile cards (when not in grouped search mode) ── */}
            {data && isMobile && !(searchText && !userChangedSort && !bulkMode && members.length > 0) && (
              <>
                <IonList lines="none">
                  {members.map((member) => (
                    <div key={member.id} style={{ position: 'relative' }}>
                      {bulkMode && <input type="checkbox" checked={selectedIds.has(member.id)} onChange={() => toggleSelectOne(member.id)} style={{ position: 'absolute', left: 4, top: '50%', transform: 'translateY(-50%)', zIndex: 2, width: 18, height: 18 }} />}
                      <MemberCard member={member} onClick={() => bulkMode ? toggleSelectOne(member.id) : navigateToMember(member.id)} />
                    </div>
                  ))}
                </IonList>
                {members.length === 0 && (
                  <div className="crm-empty-state">
                    <div className="crm-empty-state__icon"><IonIcon icon={people} /></div>
                    <h3 className="crm-empty-state__title">Niciun membru găsit</h3>
                    <p className="crm-empty-state__text">Încearcă să modifici căutarea sau filtrele aplicate.</p>
                  </div>
                )}
                {totalPages > 1 && (
                  <div className="crm-pagination" style={{ paddingBottom: 80 }}>
                    <button className="crm-pagination__btn" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
                      <IonIcon icon={chevronBack} style={{ fontSize: 12 }} />
                    </button>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#475569', padding: '0 8px' }}>{currentPage} / {totalPages}</span>
                    <button className="crm-pagination__btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
                      <IonIcon icon={chevronForward} style={{ fontSize: 12 }} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── FAB ── */}
        {isAdmin && !bulkMode && (
          <IonFab vertical="bottom" horizontal="end" slot="fixed">
            <IonFabButton onClick={() => router.push('/members/new')}><IonIcon icon={add} /></IonFabButton>
          </IonFab>
        )}

        {/* ══════ FILTER DIALOG ══════ */}
        {showFilterDialog && (
          <div className="logout-overlay" onClick={() => setShowFilterDialog(false)}>
            <div className="logout-dialog" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520, width: '92%', maxHeight: '85vh', overflow: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Filtre</h3>
                <button onClick={() => setShowFilterDialog(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                  <IonIcon icon={close} style={{ fontSize: 22, color: '#94a3b8' }} />
                </button>
              </div>

              {/* Status */}
              <div className="form-field" style={{ marginBottom: 14 }}>
                <label className="form-field__label">Status Bisericesc</label>
                <select className="form-input" value={draftStatus} onChange={(e) => setDraftStatus(e.target.value as ChurchStatus | '')}>
                  <option value="">Toate</option>
                  {Object.values(ChurchStatus).map((s) => <option key={s} value={s}>{CHURCH_STATUS_LABELS[s]}</option>)}
                </select>
              </div>

              {/* Type + Gender row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div className="form-field">
                  <label className="form-field__label">Tip Membru</label>
                  <select className="form-input" value={draftType} onChange={(e) => setDraftType(e.target.value as MembershipType | '')}>
                    <option value="">Toate</option>
                    {Object.values(MembershipType).map((t) => <option key={t} value={t}>{MEMBERSHIP_TYPE_LABELS[t]}</option>)}
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-field__label">Gen</label>
                  <select className="form-input" value={draftGender} onChange={(e) => setDraftGender(e.target.value as Gender | '')}>
                    <option value="">Toate</option>
                    {Object.values(Gender).map((g) => <option key={g} value={g}>{GENDER_LABELS[g]}</option>)}
                  </select>
                </div>
              </div>

              {/* Civil status + Sector */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div className="form-field">
                  <label className="form-field__label">Stare Civilă</label>
                  <select className="form-input" value={draftCivil} onChange={(e) => setDraftCivil(e.target.value as CivilStatusFilter | '')}>
                    <option value="">Toate</option>
                    {Object.entries(CIVIL_STATUS_LABELS_ALL).map(([c, label]) => <option key={c} value={c}>{label}</option>)}
                  </select>
                </div>
                {!sectorIdFromUrl && (
                  <div className="form-field">
                    <label className="form-field__label">Sector</label>
                    <select className="form-input" value={draftSectorId ?? ''} onChange={(e) => setDraftSectorId(e.target.value || undefined)}>
                      <option value="">Toate</option>
                      {sectors?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* Age range */}
              <div className="form-field" style={{ marginBottom: 14 }}>
                <label className="form-field__label">Interval Vârstă</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="number" className="form-input" placeholder="Min" value={draftMinAge} onChange={(e) => setDraftMinAge(e.target.value)} min={0} max={120} style={{ flex: 1 }} />
                  <span style={{ color: '#94a3b8' }}>—</span>
                  <input type="number" className="form-input" placeholder="Max" value={draftMaxAge} onChange={(e) => setDraftMaxAge(e.target.value)} min={0} max={120} style={{ flex: 1 }} />
                </div>
              </div>

              {/* Recently updated */}
              <div className="form-field" style={{ marginBottom: 14 }}>
                <label className="form-field__label">Actualizat recent</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {([['', 'Oricând'], ['7', '7 zile'], ['30', '30 zile'], ['90', '90 zile']] as const).map(([val, label]) => {
                    const active = draftUpdatedWithin === val;
                    return (
                      <button
                        key={val || 'any'}
                        type="button"
                        onClick={() => setDraftUpdatedWithin(val)}
                        style={{
                          padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                          cursor: 'pointer', fontFamily: 'inherit',
                          background: active ? '#4f46e5' : '#f1f5f9',
                          color: active ? '#fff' : '#475569',
                          border: `1px solid ${active ? '#4f46e5' : '#e2e8f0'}`,
                          transition: 'all .15s',
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Labels */}
              <div className="form-field" style={{ marginBottom: 24 }}>
                <label className="form-field__label">Etichete</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {Object.entries(LABEL_FLAG_LABELS).map(([key, label]) => (
                    <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569', cursor: 'pointer', padding: '6px 10px', borderRadius: 8, background: draftLabels[key] ? '#fef3c7' : '#f8fafc', border: `1px solid ${draftLabels[key] ? '#fde68a' : '#e2e8f0'}`, transition: 'all .15s' }}>
                      <input type="checkbox" checked={!!draftLabels[key]} onChange={(e) => setDraftLabels((p) => ({ ...p, [key]: e.target.checked }))} style={{ width: 14, height: 14 }} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between' }}>
                <button className="form-btn form-btn--secondary" onClick={resetDraftFilters}>Resetează</button>
                <button className="form-btn form-btn--primary" onClick={applyFilters} style={{ minWidth: 100 }}>
                  Aplică Filtre
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Applying filters overlay ── */}
        {/* ── Applying filters overlay ── */}
        {isApplyingFilters && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(255,255,255,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', padding: '12px 24px', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,.08)' }}>
              <IonSpinner name="crescent" style={{ width: 20, height: 20 }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: '#475569' }}>Se aplică filtrele...</span>
            </div>
          </div>
        )}

        {/* ── Export overlay — blocks all interaction ── */}
        {isExporting && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, background: '#fff', padding: '28px 40px', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,.15)' }}>
              <IonSpinner name="crescent" style={{ width: 28, height: 28, color: '#5e81f4' }} />
              <span style={{ fontSize: 15, fontWeight: 700, color: '#1c1d21' }}>Se exportă datele...</span>
              <span style={{ fontSize: 12, color: '#94a3b8' }}>Vă rugăm așteptați</span>
            </div>
          </div>
        )}

        {/* ── Export Action Sheet ── */}
        <IonActionSheet
          isOpen={showExportSheet}
          onDidDismiss={() => setShowExportSheet(false)}
          header="Exportă lista filtrată"
          buttons={[
            { text: 'Excel (.xlsx)', handler: () => { void handleExport('excel'); } },
            { text: 'CSV (.csv)', handler: () => { void handleExport('csv'); } },
            { text: 'Anulează', role: 'cancel' as const },
          ]}
        />

        {/* ── Bulk Action Sheet ── */}
        <IonActionSheet
          isOpen={showBulkActions}
          onDidDismiss={() => setShowBulkActions(false)}
          header={`Editare în masă (${selectedIds.size} selectați)`}
          buttons={[
            ...(sectors ?? []).map((sector) => ({
              text: `Mută în ${sector.name}`,
              handler: () => { void handleBulkUpdate('sector_id', sector.id); },
            })),
            { text: 'Setează status: Activ', handler: () => { void handleBulkUpdate('church_status', ChurchStatus.ACTIVE); } },
            { text: 'Setează tip: Membru', handler: () => { void handleBulkUpdate('membership_type', MembershipType.MEMBER); } },
            { text: 'Setează tip: Aparținător', handler: () => { void handleBulkUpdate('membership_type', MembershipType.DEPENDENT); } },
            { text: 'Anulează', role: 'cancel' as const },
          ]}
        />

        {/* ── Pastoral list entry editor ── */}
        <OutreachEntryModal
          isOpen={!!editingOutreach}
          member={editingOutreach}
          onClose={() => { setEditingOutreach(null); void refetch(); }}
        />
      </IonContent>
    </IonPage>
  );
};

export default MemberListPage;
